import React, { useEffect, useState } from 'react';
import { Button, Col, Dropdown, DropdownToggle, DropdownMenu, Form, FormInput, InputGroup, InputGroupAddon, InputGroupText, ListGroup, ListGroupItem, Row } from 'shards-react';
import ReactGA from 'react-ga';
import QueryInput from './QueryInput';
import SearchButton from './SearchButton';
import SelectCustomer from './SelectCustomer';
import SelectCertIssueDate from './SelectCertIssueDate';
import SelectMakerJa from './SelectMakerJa';
import SelectMakerEn from './SelectMakerEn';
import SelectTypeJa from './SelectTypeJa';
import SelectTypeEn from './SelectTypeEn';
import TooltipHint from './TooltipHint';
import { useTranslation } from 'react-i18next';
import useLocales from '../../../hooks/useLocales';
import { useLocation, useHistory } from 'react-router-dom';
import useDropdownResize from '../../../hooks/mill-sheet-search/useDropdownResize';
import useUserRoles from '../../../hooks/useUserRoles';
import useUpdateOptionStateWithQuery from '../../../hooks/mill-sheet-search/useUpdateOptionStateWithQuery';
import useConstructedQuery from '../../../hooks/mill-sheet-search/useConstructedQuery';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { searchMillSheet } from '../../../redux/slices/millSheetSearch';
import getBusinessTypeInSearchParams from '../../../utils/mill-sheet-search/getBusinessTypeInSearchParams';
import { BUSINESS_TYPE, SEARCH_FIELD_NAMES } from '../../../constants';
import type { SearchFieldName, SearchOptions } from '../../../types/millSheetSearch';
import type { SearchOptionsFragment } from '../../../types/millSheetSearch';
import type { BusinessType } from '../../../types/common';
import SelectMillJa from './SelectMillJa';
import SelectMillEn from './SelectMillEn';
import {STEEL_MAKER_NAME} from '../../../constants';

const ManageSearchOptions = (): React.ReactElement => {
  const defaultSearchOptions: SearchOptions = {
    feInputLnaguage: '',
    feInputMakerNameJa: '',
    feInputMakerNameEn: '',
    feInputMillJa: '',
    feInputMillEn: '',
    feInputMaterialTypeJa: '',
    feInputMaterialTypeEn: '',
    feInputTradeOrg: '',
    feInputCertIssueStartDate: '',
    feInputCertIssueEndDate: '',
    feInputOrderNumber: '',
    feInputContractNumber: '',
    feInputStandard: '',
    feInputMaterialSizeDStart: '',
    feInputMaterialSizeDEnd: '',
    feInputMaterialSizeTStart: '',
    feInputMaterialSizeTEnd: '',
    feInputMaterialSizeLStart: '',
    feInputMaterialSizeLEnd: '',
    feInputMeltNumber: '',
    feInputPackageId: '',
    feInputDestination: '',
    feInputCustomer1: '',
    feInputCustomer2: '',
    feInputCustomer3: '',
    feInputPlaceCode: '',
    feInputCertNumber: '',
    feInputBundleNumber: '',
  };

  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { currentLang } = useLocales();
  const { isLoading, selectedBusinessType, selectedSteelCategory } = useSelector((state: RootState) => state.millSheetSearch);
  const { steelCategoryType } = useSelector((state: RootState) => state.options);
  const dropdownMenuStyle = useDropdownResize();

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // 文字列クエリ
  const [query, setQuery] = useState<string>('');
  const [optionSet, setOptionSet] = useState<SearchOptions>(defaultSearchOptions);
  // クエリによってupdateされた検索オプション
  const updatedOptionState: SearchOptionsFragment = useUpdateOptionStateWithQuery(query);
  // 文字列クエリと検索オプションを元に構築されるSearch Param
  const constructedQuery = useConstructedQuery(optionSet, query);
  // なりすまし検索パーツの表示制御（商社アドミンかパワーユーザの場合のみ利用可能）
  const { isAdminOrPoweruser } = useUserRoles();

  // TODO: 検索オプションの有効・無効制御は下記のような2層構造で制御する
  // ========================================
  // 第1層：メーカー単位のプリミティブフラグ（1メーカー = 1フラグ）（上記の通り）
  // const isNipponSteelSelected = optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.NIPPONSTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.NIPPONSTEEL.EN;                                                 
  const isAichiSelected = optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.AICHISTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.AICHISTEEL.EN;
  // const isKobeSelected = optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.KOBESTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.KOBESTEEL.EN;
  // const isJfeSelected = optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.JFESTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.JFESTEEL.EN;
  // 第2層：フィールド単位の制御フラグ（第1層を合成して作る）
  // 例）const isPackageIdEnabled = !isAichiSelected && !isJfeSelected;
  // ========================================

  // 日鉄以外が選択されている場合には厚さと長さの指定をできなくする
  // const isSizeTEnabled: boolean = optionSet.feInputMakerNameJa === '日本製鉄' || optionSet.feInputMakerNameEn === 'NIPPON_STEEL';
  // const isSizeLEnabled: boolean = optionSet.feInputMakerNameJa === '日本製鉄' || optionSet.feInputMakerNameEn === 'NIPPON_STEEL';
  // 検索オプションの表示切り替えに使うフラグ
  const isDomesticSelected: boolean = selectedBusinessType === BUSINESS_TYPE.DOMESTIC;
  // 「輸出は、SC No.(商社契約番号、全鉄鋼M共通)、Bundle№(輸出愛知のみ)を表示する」の実装用フラグ
  const isExportSelected: boolean = selectedBusinessType === BUSINESS_TYPE.EXPORT;
  // 日鉄以外が選択されている場合にはBundle Noの指定をできなくする
  // リファクタリングを検討する。forで処理出来るようにする？
  const isBundleNoEnabled: boolean =
    optionSet.feInputMakerNameJa !== STEEL_MAKER_NAME.NIPPONSTEEL.JA && optionSet.feInputMakerNameJa !== STEEL_MAKER_NAME.KOBESTEEL.JA && optionSet.feInputMakerNameJa !== STEEL_MAKER_NAME.JFESTEEL.JA &&
    optionSet.feInputMakerNameEn !== STEEL_MAKER_NAME.NIPPONSTEEL.EN && optionSet.feInputMakerNameEn !== STEEL_MAKER_NAME.KOBESTEEL.EN && optionSet.feInputMakerNameEn !== STEEL_MAKER_NAME.JFESTEEL.EN;
  // 愛知 or 神戸 or JFE選択時に検索オプションを制御するためのフラグ。
  // 愛知 or 神戸 or JFE選択時には「需要家2, 3」「国名」の検索オプションは利用できない
  const isAichiOrKobeOrJfeSelected: boolean =
    optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.AICHISTEEL.JA || optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.KOBESTEEL.JA || optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.JFESTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.AICHISTEEL.EN || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.KOBESTEEL.EN || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.JFESTEEL.EN;
  // JFE選択判定用フラグ
  const isJfeSelected: boolean = optionSet.feInputMakerNameJa === STEEL_MAKER_NAME.JFESTEEL.JA || optionSet.feInputMakerNameEn === STEEL_MAKER_NAME.JFESTEEL.EN;
  // 受渡場所コード制御用フラグ（輸出区分でJFE選択時のみ無効化）
  const isDeliverySiteEnabled: boolean = !(isExportSelected && isJfeSelected);
  // 検査番号制御用フラグ（愛知 or JFE選択時に無効化）
  const isPackageIdEnabled = !isAichiSelected && !isJfeSelected;

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  // 確認用（検索オプション）
  // useEffect(() => {
  //   console.log('search option:', optionSet);
  // }, [optionSet]);

  // 確認用（内部Search param）
  // useEffect(() => {
  //   console.log('constructed query:', constructedQuery);
  // }, [constructedQuery]);

  // 区分が変更されたら検索オプションを最新化する
  useEffect(() => {
    // console.log(selectedBusinessType);
    const selectedLang: string = selectedBusinessType === BUSINESS_TYPE.EXPORT ? 'EN' : 'JP';
    const newOptionSet: SearchOptions = Object.assign({}, optionSet, { feInputLanguage: selectedLang });
    setOptionSet(newOptionSet);
  }, [selectedBusinessType]);

  // クエリ文字列が変更されたら検索オプションを最新化する
  useEffect(() => {
    // console.log(updatedOptionState);
    const selectedLang: string = selectedBusinessType === BUSINESS_TYPE.EXPORT ? 'EN' : 'JP';
    const newOptionSet: SearchOptions = Object.assign({}, defaultSearchOptions, { feInputLanguage: selectedLang }, updatedOptionState);
    setOptionSet(newOptionSet);
  }, [updatedOptionState]);

  // URLが変わったら検索する
  useEffect(() => {
    // URLクリックで画面を開いたときはURLからクエリを生成して検索する
    // そうでなければクエリは作られているのでそのまま検索する
    const params = new URLSearchParams(location.search);
    const q = decodeURIComponent(params.get('q'));
    // console.log(q);
    if (q === 'null') {
      // Search Paramsがnull（ログイン直後）なら何もしない
    } else {
      // qに区分が含まれるかどうかを調べる
      const businessTypeInSearchParams: BusinessType | undefined = getBusinessTypeInSearchParams(q);
      // 区分がなければ追加クエリを準備する
      const businessTypeQuery: string = !businessTypeInSearchParams ? (q !== '' ? 'Language:JP AND ' : 'Language:JP') : '';
      // console.log(businessTypeQuery);
      // qに区分が含まれている場合はqは変えない
      const newSearchParam: string = businessTypeQuery + q;
      // console.log(businessTypeInSearchParams);
      // console.log(newSearchParam);
      // console.log(location.search);
      if (!businessTypeInSearchParams) {
        params.set('q', encodeURIComponent(newSearchParam));
        history.push({ search: params.toString() });
      } else {
        const organizationIds = isAdminOrPoweruser ? decodeURIComponent(params.get('organizationIds')) : '';
        dispatch(searchMillSheet(newSearchParam, organizationIds, isAdminOrPoweruser));
      }
    }
  }, [location.search]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * 検索オプションメニューを開閉したとき
   */
  const handleToggleMenuOpen = (): void => {
    setIsMenuOpen(!isMenuOpen);
  };

  /**
   * クエリ文字列を変更したとき
   */
  const handleQueryChange = (value: string): void => {
    setIsDirty(true);
    setQuery(value);
  };

  /**
   * テキスト入力を変更したとき
   */
  const handleTextChange = (event: React.ChangeEvent<{ name: SearchFieldName; value: string }>): void => {
    setIsDirty(true);
    const fieldName: string = event.target.name;
    const newValue: string = event.target.value;
    const newOptionSet = Object.assign({}, optionSet, { [fieldName]: newValue });
    setOptionSet(newOptionSet);
  };

  /**
   * プルダウンを変更したとき
   */
  const handleSelectChange = (name: SearchFieldName, value: string) => {
    setIsDirty(true);
    const newOptionSet = Object.assign({}, optionSet, { [name]: value });
    setOptionSet(newOptionSet);
  };

  /**
   * プルダウンをクリアしたとき
   */
  const handleClearSelect = (name: SearchFieldName): void => {
    setIsDirty(true);
    const newOptionSet = Object.assign({}, optionSet, { [name]: '' });
    setOptionSet(newOptionSet);
  };

  /**
   * クリアボタンを押したとき
   */
  const handleClelarAll = (): void => {
    setOptionSet(defaultSearchOptions);
  };

  /**
   * 文字列クエリでEnterを押すか、検索ボタンを押したとき
   */
  const handleSearch = (event: React.FormEvent | React.MouseEvent): void => {
    event.preventDefault();
    ReactGA.ga('send', 'event', 'millsheetSearch', 'querySubmit', 'searchButton');
    // change url
    const params = new URLSearchParams(location.search);
    params.set('q', encodeURIComponent(constructedQuery));
    history.push({ search: params.toString() });
    setIsMenuOpen(false);
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <Col id="mainSearchArea" md="10" className="form-group">
        <Form onSubmit={handleSearch}>
          <InputGroup seamless>
            {/* クエリ文字列 */}
            {/* ---------------------------------------- */}
            <QueryInput language={currentLang.value} selectedSteelCategory={selectedSteelCategory} steelCategoryType={steelCategoryType} onChangeQuery={handleQueryChange} />
            <Dropdown open={isMenuOpen} toggle={handleToggleMenuOpen} addonType="append">
              <DropdownToggle split theme="secondary">
                {t('millSheet.option')} &nbsp;
              </DropdownToggle>
              <DropdownMenu right>
                <ListGroup id="searchOptionDropdownMenu" flush style={dropdownMenuStyle}>
                  <ListGroupItem className="p-4">
                    <Row>
                      <Col md="6" className="form-group">
                        <Row form>
                          {/* メーカー名 */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'ja' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MAKER_NAME_JA}>{isDomesticSelected ? 'メーカー名' : 'Steel Maker'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectMakerJa value={optionSet.feInputMakerNameJa} onChangeMaker={handleSelectChange} />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MAKER_NAME_JA)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* STEEL_MAKER_EN */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'en' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MAKER_NAME_EN}>{isDomesticSelected ? 'STEEL MAKER' : 'Steel Maker'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectMakerEn value={optionSet.feInputMakerNameEn} onChangeMaker={handleSelectChange} />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MAKER_NAME_EN)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* ミル情報 */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'ja' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MILL_JA}>{isDomesticSelected ? 'ミル' : 'Plant'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectMillJa selectedSteelMakerJa={optionSet.feInputMakerNameJa} value={optionSet.feInputMillJa} onChangeMill={handleSelectChange} />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MILL_JA)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* MILL_EN */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'en' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MILL_EN}>{isDomesticSelected ? 'ミル' : 'Plant'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectMillEn selectedSteelMakerEn={optionSet.feInputMakerNameEn} value={optionSet.feInputMillEn} onChangeMill={handleSelectChange} />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MILL_EN)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* 品種 */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'ja' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MATERIAL_TYPE_JA}>{isDomesticSelected ? '品種' : 'Products'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectTypeJa
                                  value={optionSet.feInputMaterialTypeJa}
                                  language={currentLang.value}
                                  selectedSteelCategory={selectedSteelCategory}
                                  selectedSteelMakerJa={optionSet.feInputMakerNameJa}
                                  steelCategoryType={steelCategoryType}
                                  onChangeType={handleSelectChange}
                                />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MATERIAL_TYPE_JA)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* STEEL_TYPE_EN */}
                          {/* ---------------------------------------- */}
                          {currentLang.value === 'en' && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.MATERIAL_TYPE_EN}>{isDomesticSelected ? 'STEEL TYPE' : 'Products'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectTypeEn
                                  value={optionSet.feInputMaterialTypeEn}
                                  language={currentLang.value}
                                  selectedSteelCategory={selectedSteelCategory}
                                  selectedSteelMakerEn={optionSet.feInputMakerNameEn}
                                  steelCategoryType={steelCategoryType}
                                  onChangeType={handleSelectChange}
                                />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.MATERIAL_TYPE_EN)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* 商社店部課 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.TRADE_ORG}>{isDomesticSelected ? '商社店部課' : 'Section Code'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput id={SEARCH_FIELD_NAMES.TRADE_ORG} name={SEARCH_FIELD_NAMES.TRADE_ORG} value={optionSet.feInputTradeOrg} placeholder="例:226" onChange={handleTextChange} />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.TRADE_ORG} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 証明書発行年月日 */}
                          {/* ---------------------------------------- */}
                          <SelectCertIssueDate isDomestic={isDomesticSelected} start={optionSet.feInputCertIssueStartDate} end={optionSet.feInputCertIssueEndDate} onChangeDate={handleSelectChange} />
                          {/* 契約番号（商社） */}
                          {/* ---------------------------------------- */}
                          {isExportSelected && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.ORDER_NO}>{isDomesticSelected ? '契約番号（商社）' : 'SC No.'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <FormInput
                                  id={SEARCH_FIELD_NAMES.ORDER_NO}
                                  name={SEARCH_FIELD_NAMES.ORDER_NO}
                                  value={optionSet.feInputOrderNumber}
                                  placeholder="例:123-AB0123"
                                  onChange={handleTextChange}
                                />
                              </Col>
                              <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                                <TooltipHint id={SEARCH_FIELD_NAMES.ORDER_NO} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                              </Col>
                            </>
                          )}
                          {/* 契約番号 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.CONTRACT_NO}>{isDomesticSelected ? '契約番号' : 'Contract No.'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.CONTRACT_NO}
                              name={SEARCH_FIELD_NAMES.CONTRACT_NO}
                              value={optionSet.feInputContractNumber}
                              placeholder="例:9205E11OA28909"
                              onChange={handleTextChange}
                            />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.CONTRACT_NO} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 規格 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.STANDARD}>{isDomesticSelected ? '規格' : 'Grade'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput id={SEARCH_FIELD_NAMES.STANDARD} name={SEARCH_FIELD_NAMES.STANDARD} value={optionSet.feInputStandard} placeholder="例:S43CVS1" onChange={handleTextChange} />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.STANDARD} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 寸法_径 */}
                          {/* ---------------------------------------- */}
                          <Col md="3" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_START}>{isDomesticSelected ? '寸法' : 'Size'}</label>
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_START}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_START}
                              value={optionSet.feInputMaterialSizeDStart}
                              placeholder="例:75"
                              onChange={handleTextChange}
                            />
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_END}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_END}
                              value={optionSet.feInputMaterialSizeDEnd}
                              placeholder="例:75"
                              onChange={handleTextChange}
                            />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_D_START} hint="半角の丸カッコ「()」で囲んで「OR」で区切ると複数検索が可能です（例: (10 OR 20 OR 30)）" />
                          </Col>
                          {/* 厚さ */}
                          {/* ---------------------------------------- */}
                          {/* <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_START}>厚さ</label>
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              inline={true}
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_START}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_START}
                              value={optionSet.feInputMaterialSizeTStart}
                              placeholder="例:75"
                              onChange={handleTextChange}
                              disabled={!isSizeTEnabled}
                            />
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_END}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_T_END}
                              value={optionSet.feInputMaterialSizeTEnd}
                              placeholder="例:75"
                              onChange={handleTextChange}
                              disabled={!isSizeTEnabled}
                            />
                          </Col> */}
                          {/* 長さ */}
                          {/* ---------------------------------------- */}
                          {/* <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_START}>長さ</label>
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              inline={true}
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_START}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_START}
                              value={optionSet.feInputMaterialSizeLStart}
                              placeholder="例:75"
                              onChange={handleTextChange}
                              disabled={!isSizeLEnabled}
                            />
                          </Col>
                          <Col md="4" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_END}
                              name={SEARCH_FIELD_NAMES.MATERIAL_SIZE_L_END}
                              value={optionSet.feInputMaterialSizeLEnd}
                              placeholder="例:75"
                              onChange={handleTextChange}
                              disabled={!isSizeLEnabled}
                            />
                          </Col> */}
                        </Row>
                      </Col>
                      <Col md="6" className="form-group">
                        <Row form>
                          {/* 溶解番号 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.MELT_NO}>{isDomesticSelected ? '溶解番号' : 'Heat No.'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput id={SEARCH_FIELD_NAMES.MELT_NO} name={SEARCH_FIELD_NAMES.MELT_NO} value={optionSet.feInputMeltNumber} placeholder="例:9J0911" onChange={handleTextChange} />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.MELT_NO} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 検査番号 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.PACKAGE_ID}>{isDomesticSelected ? '検査番号' : 'Inspection Number'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.PACKAGE_ID}
                              name={SEARCH_FIELD_NAMES.PACKAGE_ID}
                              value={optionSet.feInputPackageId}
                              placeholder="例:3YE62864"
                              onChange={handleTextChange}
                              disabled={!isPackageIdEnabled}
                            />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.PACKAGE_ID} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 国名 */}
                          {/* ---------------------------------------- */}
                          {isExportSelected && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.DESTINATION}>{isDomesticSelected ? '国名' : 'Destination'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <FormInput
                                  id={SEARCH_FIELD_NAMES.DESTINATION}
                                  name={SEARCH_FIELD_NAMES.DESTINATION}
                                  value={optionSet.feInputDestination}
                                  placeholder="例:INDIA"
                                  onChange={handleTextChange}
                                  disabled={isAichiOrKobeOrJfeSelected}
                                />
                              </Col>
                              <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                                <TooltipHint id={SEARCH_FIELD_NAMES.DESTINATION} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                              </Col>
                            </>
                          )}
                          {/* 需要家１ */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor="feInputCustomer1">{isDomesticSelected ? '需要家１' : 'Customer1'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <SelectCustomer name={SEARCH_FIELD_NAMES.CUSTOMER1} value={optionSet.feInputCustomer1} onChangeCustomer={handleSelectChange} />
                          </Col>
                          <Col md="1" className="form-group">
                            <InputGroupAddon type="append" className="button-clear">
                              <InputGroupText>
                                <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.CUSTOMER1)}>
                                  cancel
                                </i>
                              </InputGroupText>
                            </InputGroupAddon>
                          </Col>
                          {/* 需要家２ */}
                          {/* ---------------------------------------- */}
                          <>
                            <Col md="4" className="form-group">
                              <label htmlFor="feInputCustomer2">{isDomesticSelected ? '需要家２' : 'Customer2'}</label>
                            </Col>
                            <Col md="7" className="form-group">
                              <SelectCustomer name={SEARCH_FIELD_NAMES.CUSTOMER2} value={optionSet.feInputCustomer2} onChangeCustomer={handleSelectChange} disabled={isAichiOrKobeOrJfeSelected} />
                            </Col>
                            <Col md="1" className="form-group">
                              <InputGroupAddon type="append" className="button-clear">
                                <InputGroupText>
                                  <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.CUSTOMER2)}>
                                    cancel
                                  </i>
                                </InputGroupText>
                              </InputGroupAddon>
                            </Col>
                          </>
                          {/* 需要家３ */}
                          {/* ---------------------------------------- */}
                          {isDomesticSelected && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor="feInputCustomer3">{isDomesticSelected ? '需要家３' : 'Customer3'}</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <SelectCustomer name={SEARCH_FIELD_NAMES.CUSTOMER3} value={optionSet.feInputCustomer3} onChangeCustomer={handleSelectChange} disabled={isAichiOrKobeOrJfeSelected} />
                              </Col>
                              <Col md="1" className="form-group">
                                <InputGroupAddon type="append" className="button-clear">
                                  <InputGroupText>
                                    <i className="material-icons" onClick={() => handleClearSelect(SEARCH_FIELD_NAMES.CUSTOMER3)}>
                                      cancel
                                    </i>
                                  </InputGroupText>
                                </InputGroupAddon>
                              </Col>
                            </>
                          )}
                          {/* 受渡場所コード */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.PLACE_CODE}>{isDomesticSelected ? '受渡場所コード' : 'Delivery Site'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput id={SEARCH_FIELD_NAMES.PLACE_CODE} name={SEARCH_FIELD_NAMES.PLACE_CODE} value={optionSet.feInputPlaceCode} placeholder="例:14I04" onChange={handleTextChange} disabled={!isDeliverySiteEnabled} />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.PLACE_CODE} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* 証明書番号 */}
                          {/* ---------------------------------------- */}
                          <Col md="4" className="form-group">
                            <label htmlFor={SEARCH_FIELD_NAMES.CERT_NO}>{isDomesticSelected ? '証明書番号' : 'Certificate No.'}</label>
                          </Col>
                          <Col md="7" className="form-group">
                            <FormInput
                              id={SEARCH_FIELD_NAMES.CERT_NO}
                              name={SEARCH_FIELD_NAMES.CERT_NO}
                              value={optionSet.feInputCertNumber}
                              placeholder="例:JD35400045-01"
                              onChange={handleTextChange}
                            />
                          </Col>
                          <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                            <TooltipHint id={SEARCH_FIELD_NAMES.CERT_NO} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                          </Col>
                          {/* Bundle No */}
                          {/* ---------------------------------------- */}
                          {isExportSelected && isBundleNoEnabled && (
                            <>
                              <Col md="4" className="form-group">
                                <label htmlFor={SEARCH_FIELD_NAMES.BUNDLE_NO}>Bundle No</label>
                              </Col>
                              <Col md="7" className="form-group">
                                <FormInput
                                  id={SEARCH_FIELD_NAMES.BUNDLE_NO}
                                  name={SEARCH_FIELD_NAMES.BUNDLE_NO}
                                  value={optionSet.feInputBundleNumber}
                                  placeholder="例:TT8000"
                                  onChange={handleTextChange}
                                  disabled={!isBundleNoEnabled}
                                />
                              </Col>
                              <Col md="1" className="form-group d-flex align-items-center justify-content-center">
                                <TooltipHint id={SEARCH_FIELD_NAMES.BUNDLE_NO} hint="半角の縦棒「|」で区切って複数検索が可能です（例: A|B|C）" />
                              </Col>
                            </>
                          )}
                        </Row>
                      </Col>
                    </Row>
                    <Row form>
                      <Col md="3">
                        <Button block theme="light" onClick={handleClelarAll}>
                          {t('dialog.clear')}
                        </Button>
                      </Col>
                      <Col md="6"></Col>
                      <Col md="3">
                        <SearchButton isLoading={isLoading} onClick={handleSearch} />
                      </Col>
                    </Row>
                  </ListGroupItem>
                </ListGroup>
              </DropdownMenu>
            </Dropdown>
          </InputGroup>
        </Form>
      </Col>
      <Col md="2" className="form-group">
        <SearchButton isLoading={isLoading} onClick={handleSearch} />
      </Col>
    </>
  );
};

export default ManageSearchOptions;
